import { database } from '../config/backend';
import { ref, get as rtdbGet, set as rtdbSet, push, query, orderByKey, limitToLast, remove as rtdbRemove } from './phpDataAdapter';
import { FoodItem, calculateNutritionalValues } from './foodDatabaseService';

export type MealType = 'breakfast' | 'morningSnack' | 'lunch' | 'afternoonSnack' | 'dinner' | 'eveningSnack';

export interface MealEntry {
  id: string;
  foodId: string;
  foodName: string;
  quantity: number;
  unit: FoodItem['servingUnit'];
  nutritionalInfo: FoodItem['nutritionalInfo'];
  notes?: string;
  timestamp: string;
}

export interface DailySummary {
  totalCalories: number;
  totalProtein: number;
  totalCarbs: number;
  totalFats: number;
  waterIntake: number; // em ml
  notes?: string;
}

export interface FoodDiaryEntry {
  date: string; // formato YYYY-MM-DD
  meals: {
    [key in MealType]: MealEntry[];
  };
  dailySummary: DailySummary;
  createdAt: string;
  updatedAt: string;
}

/**
 * Formata data para YYYY-MM-DD
 */
export function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Busca o diário alimentar de um paciente para uma data específica
 */
export async function getFoodDiaryEntry(
  patientId: string,
  date: string
): Promise<FoodDiaryEntry | null> {
  try {
    console.log(`[foodDiaryService] Buscando diário para paciente ${patientId} na data ${date}`);
    
    // Primeiro tenta buscar em foodDiary (formato novo)
    let entryRef = ref(database, `foodDiary/${patientId}/${date}`);
    let snapshot = await rtdbGet(entryRef);
    console.log(`[foodDiaryService] Tentativa 1 - foodDiary/${patientId}/${date}:`, snapshot.exists() ? 'encontrado' : 'não encontrado');
    
    // Se não encontrar, tenta buscar em users/{patientId}/dietTracking (formato do app mobile)
    if (!snapshot.exists()) {
      entryRef = ref(database, `users/${patientId}/dietTracking/${date}`);
      snapshot = await rtdbGet(entryRef);
      console.log(`[foodDiaryService] Tentativa 2 - users/${patientId}/dietTracking/${date}:`, snapshot.exists() ? 'encontrado' : 'não encontrado');
    }
    
    if (!snapshot.exists()) {
      console.log(`[foodDiaryService] Nenhum registro encontrado para paciente ${patientId} na data ${date}`);
      return null;
    }
    
    const data = snapshot.val();
    
    // Se os dados vieram de dietTracking, pode ter formato diferente
    // Verifica se já está no formato FoodDiaryEntry
    if (data.dailySummary && data.meals && typeof data.meals === 'object' && !Array.isArray(data.meals)) {
      // Já está no formato correto
      return {
        date,
        meals: data.meals || {
          breakfast: [],
          morningSnack: [],
          lunch: [],
          afternoonSnack: [],
          dinner: [],
          eveningSnack: [],
        },
        dailySummary: data.dailySummary || {
          totalCalories: 0,
          totalProtein: 0,
          totalCarbs: 0,
          totalFats: 0,
          waterIntake: 0,
        },
        createdAt: data.createdAt || new Date().toISOString(),
        updatedAt: data.updatedAt || new Date().toISOString(),
      };
    }
    
    // Se veio de dietTracking, pode precisar converter o formato
    // Por enquanto, retorna null se não estiver no formato esperado
    // (o sistema web pode criar uma nova entrada)
    return null;
  } catch (error) {
    console.error('Erro ao buscar entrada do diário:', error);
    return null;
  }
}

/**
 * Cria ou atualiza uma entrada do diário alimentar
 */
export async function saveFoodDiaryEntry(
  patientId: string,
  entry: Partial<FoodDiaryEntry>
): Promise<void> {
  try {
    if (!entry.date) {
      throw new Error('Data é obrigatória');
    }
    
    // Salva em foodDiary (formato novo para web)
    const entryRef = ref(database, `foodDiary/${patientId}/${entry.date}`);
    const existing = await getFoodDiaryEntry(patientId, entry.date);
    
    const entryData: FoodDiaryEntry = {
      date: entry.date,
      meals: entry.meals || existing?.meals || {
        breakfast: [],
        morningSnack: [],
        lunch: [],
        afternoonSnack: [],
        dinner: [],
        eveningSnack: [],
      },
      dailySummary: entry.dailySummary || existing?.dailySummary || {
        totalCalories: 0,
        totalProtein: 0,
        totalCarbs: 0,
        totalFats: 0,
        waterIntake: 0,
      },
      createdAt: existing?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    
    // Recalcular resumo diário
    entryData.dailySummary = calculateDailySummary(entryData.meals, entryData.dailySummary.waterIntake, entryData.dailySummary.notes);
    
    await rtdbSet(entryRef, entryData);
  } catch (error) {
    console.error('Erro ao salvar entrada do diário:', error);
    throw new Error('Não foi possível salvar o registro');
  }
}

/**
 * Adiciona um alimento a uma refeição
 */
export async function addFoodToMeal(
  patientId: string,
  date: string,
  mealType: MealType,
  food: FoodItem,
  quantity: number,
  unit: FoodItem['servingUnit'],
  notes?: string
): Promise<void> {
  try {
    let entry = await getFoodDiaryEntry(patientId, date);
    
    if (!entry) {
      // Criar nova entrada
      entry = {
        date,
        meals: {
          breakfast: [],
          morningSnack: [],
          lunch: [],
          afternoonSnack: [],
          dinner: [],
          eveningSnack: [],
        },
        dailySummary: {
          totalCalories: 0,
          totalProtein: 0,
          totalCarbs: 0,
          totalFats: 0,
          waterIntake: 0,
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
    }
    
    const nutritionalInfo = calculateNutritionalValues(food, quantity, unit);
    const mealEntry: MealEntry = {
      id: push(ref(database)).key as string,
      foodId: food.id,
      foodName: food.name,
      quantity,
      unit,
      nutritionalInfo,
      notes,
      timestamp: new Date().toISOString(),
    };
    
    entry.meals[mealType].push(mealEntry);
    entry.dailySummary = calculateDailySummary(entry.meals, entry.dailySummary.waterIntake, entry.dailySummary.notes);
    entry.updatedAt = new Date().toISOString();
    
    await saveFoodDiaryEntry(patientId, entry);
  } catch (error) {
    console.error('Erro ao adicionar alimento à refeição:', error);
    throw new Error('Não foi possível adicionar o alimento');
  }
}

/**
 * Remove um alimento de uma refeição
 */
export async function removeFoodFromMeal(
  patientId: string,
  date: string,
  mealType: MealType,
  entryId: string
): Promise<void> {
  try {
    const entry = await getFoodDiaryEntry(patientId, date);
    
    if (!entry) {
      throw new Error('Entrada do diário não encontrada');
    }
    
    entry.meals[mealType] = entry.meals[mealType].filter(item => item.id !== entryId);
    entry.dailySummary = calculateDailySummary(entry.meals, entry.dailySummary.waterIntake, entry.dailySummary.notes);
    entry.updatedAt = new Date().toISOString();
    
    await saveFoodDiaryEntry(patientId, entry);
  } catch (error) {
    console.error('Erro ao remover alimento da refeição:', error);
    throw new Error('Não foi possível remover o alimento');
  }
}

/**
 * Atualiza a ingestão de água
 */
export async function updateWaterIntake(
  patientId: string,
  date: string,
  waterIntake: number
): Promise<void> {
  try {
    const entry = await getFoodDiaryEntry(patientId, date);
    
    if (!entry) {
      // Criar nova entrada apenas com água
      const newEntry: FoodDiaryEntry = {
        date,
        meals: {
          breakfast: [],
          morningSnack: [],
          lunch: [],
          afternoonSnack: [],
          dinner: [],
          eveningSnack: [],
        },
        dailySummary: {
          totalCalories: 0,
          totalProtein: 0,
          totalCarbs: 0,
          totalFats: 0,
          waterIntake,
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      await saveFoodDiaryEntry(patientId, newEntry);
      return;
    }
    
    entry.dailySummary.waterIntake = waterIntake;
    entry.updatedAt = new Date().toISOString();
    
    await saveFoodDiaryEntry(patientId, entry);
  } catch (error) {
    console.error('Erro ao atualizar ingestão de água:', error);
    throw new Error('Não foi possível atualizar a ingestão de água');
  }
}

/**
 * Busca histórico do diário alimentar
 */
export async function getFoodDiaryHistory(
  patientId: string,
  startDate: string,
  endDate: string
): Promise<FoodDiaryEntry[]> {
  try {
    // Tenta buscar em foodDiary primeiro
    let entriesRef = ref(database, `foodDiary/${patientId}`);
    let snapshot = await rtdbGet(query(entriesRef, orderByKey()));
    
    // Se não encontrar, tenta em users/{patientId}/dietTracking
    if (!snapshot.exists()) {
      entriesRef = ref(database, `users/${patientId}/dietTracking`);
      snapshot = await rtdbGet(query(entriesRef, orderByKey()));
    }
    
    if (!snapshot.exists()) {
      return [];
    }
    
    const data = snapshot.val();
    const entries: FoodDiaryEntry[] = [];
    
    // Filtrar por intervalo de datas
    for (const [date, entryData] of Object.entries(data)) {
      if (date >= startDate && date <= endDate) {
        entries.push({
          date,
          meals: (entryData as any).meals || {
            breakfast: [],
            morningSnack: [],
            lunch: [],
            afternoonSnack: [],
            dinner: [],
            eveningSnack: [],
          },
          dailySummary: (entryData as any).dailySummary || {
            totalCalories: 0,
            totalProtein: 0,
            totalCarbs: 0,
            totalFats: 0,
            waterIntake: 0,
          },
          createdAt: (entryData as any).createdAt || new Date().toISOString(),
          updatedAt: (entryData as any).updatedAt || new Date().toISOString(),
        });
      }
    }
    
    return entries.sort((a, b) => b.date.localeCompare(a.date));
  } catch (error) {
    console.error('Erro ao buscar histórico do diário:', error);
    return [];
  }
}

/**
 * Calcula o resumo nutricional diário
 */
export function calculateDailySummary(
  meals: FoodDiaryEntry['meals'],
  waterIntake: number = 0,
  notes?: string
): DailySummary {
  let totalCalories = 0;
  let totalProtein = 0;
  let totalCarbs = 0;
  let totalFats = 0;
  
  Object.values(meals).forEach(mealEntries => {
    mealEntries.forEach(entry => {
      totalCalories += entry.nutritionalInfo.calories;
      totalProtein += entry.nutritionalInfo.protein;
      totalCarbs += entry.nutritionalInfo.carbs;
      totalFats += entry.nutritionalInfo.fats;
    });
  });
  
  return {
    totalCalories: Math.round(totalCalories),
    totalProtein: Math.round(totalProtein * 10) / 10,
    totalCarbs: Math.round(totalCarbs * 10) / 10,
    totalFats: Math.round(totalFats * 10) / 10,
    waterIntake,
    notes,
  };
}

/**
 * Busca estatísticas do diário alimentar
 */
export async function getFoodDiaryStats(
  patientId: string,
  days: number = 7
): Promise<{
  averageCalories: number;
  averageProtein: number;
  averageCarbs: number;
  averageFats: number;
  averageWater: number;
  totalDays: number;
}> {
  try {
    const endDate = formatDate(new Date());
    const startDate = formatDate(new Date(Date.now() - days * 24 * 60 * 60 * 1000));
    
    const entries = await getFoodDiaryHistory(patientId, startDate, endDate);
    
    if (entries.length === 0) {
      return {
        averageCalories: 0,
        averageProtein: 0,
        averageCarbs: 0,
        averageFats: 0,
        averageWater: 0,
        totalDays: 0,
      };
    }
    
    const totals = entries.reduce((acc, entry) => {
      acc.calories += entry.dailySummary.totalCalories;
      acc.protein += entry.dailySummary.totalProtein;
      acc.carbs += entry.dailySummary.totalCarbs;
      acc.fats += entry.dailySummary.totalFats;
      acc.water += entry.dailySummary.waterIntake;
      return acc;
    }, { calories: 0, protein: 0, carbs: 0, fats: 0, water: 0 });
    
    return {
      averageCalories: Math.round(totals.calories / entries.length),
      averageProtein: Math.round((totals.protein / entries.length) * 10) / 10,
      averageCarbs: Math.round((totals.carbs / entries.length) * 10) / 10,
      averageFats: Math.round((totals.fats / entries.length) * 10) / 10,
      averageWater: Math.round(totals.water / entries.length),
      totalDays: entries.length,
    };
  } catch (error) {
    console.error('Erro ao calcular estatísticas:', error);
    return {
      averageCalories: 0,
      averageProtein: 0,
      averageCarbs: 0,
      averageFats: 0,
      averageWater: 0,
      totalDays: 0,
    };
  }
}

